import { GlyphChange, GlyphState, IdeaBox, IdeaBoxEntry, TaskType } from "konect-model";
import domtoimage from 'dom-to-image';
import { GlyphEvent, GlyphEventService } from "./event-service";
import { nanoid } from "nanoid";
import { cloneDeep } from "lodash";
import JSZip from 'jszip';

var mx = require("mxgraph")({
    mxImageBasePath: "mxgraph/javascript/src/images",
    mxBasePath: "mxgraph/javascript/src"
})

export class MXGraphService {

    // Converts an mxGraph style string to an object
    public static styleStringToObject(style: string) {
        const entries = style.split(";");
        const s: { [key: string]: string } = {};
        for (const e of entries) {
            const parts = e.split("=")
            const isBool = parts.length === 1;
            if (!isBool && parts.length === 2) {
                s[parts[0]] = parts[1];
            } else if (parts[0]) {
                s[parts[0]] = "bool";
            }
        }
        return s
    }

    // Converts an object to an mxGraph conform style string
    public static styleObjectToString(styleObj: any) {
        let style = ""
        for (const key of Object.keys(styleObj)) {
            if (styleObj[key] === "removed") continue
            if (styleObj[key] !== "bool") {
                style += key + "=" + styleObj[key] + ";";
            } else {
                style += key + ";";
            }
        }
        return style
    }

    //returns a merged style where all states are applied based on the given state order
    public static getCellStyleForGlyphStates(cell: mxCell, baseState: GlyphState, states: GlyphState[]) {
        let style: any = {};
        for (const state of [baseState, ...states]) {
            const newStyle = state.changes.find(c => c.cellId === cell.id)?.style;
            if (!newStyle) continue
            Object.assign(style, newStyle);
            cell.setVisible(!newStyle.hide || newStyle.hide === "0");
        }
        return style;
    }

    public static getCellGeometryForGlyphStates(cell: mxCell, baseState: GlyphState, states: GlyphState[]) {
        const geometry = Object.assign({}, { ...baseState.changes.find(state => state.cellId === cell.id)?.geometry });
        if (!geometry) return {}
        const initialGeometry = Object.assign({}, geometry);
        let updatedPoints = false; // used to only update points of lines once
        for (let i = states.length - 1; i >= 0; i--) {
            const state = states[i];
            const stateGeometry = state.changes.find(c => c.cellId === cell.id)?.geometry;
            if (!stateGeometry) continue;

            // update the start end endpoints on lines
            if (geometry.sourcePoint && stateGeometry.sourcePoint) {
                const point = { ...geometry.sourcePoint };
                if (initialGeometry.sourcePoint.x === point.x) {
                    point.x += stateGeometry.sourcePoint.x;
                }
                if (initialGeometry.sourcePoint.y === point.y) {
                    point.y += stateGeometry.sourcePoint.y;
                }
                geometry.sourcePoint = Object.assign(new mx.mxPoint(), point);
            }
            if (geometry.targetPoint && stateGeometry.targetPoint) {
                const point = { ...geometry.targetPoint };
                if (initialGeometry.targetPoint.x === point.x) {
                    point.x += stateGeometry.targetPoint.x;
                }
                if (initialGeometry.targetPoint.y === point.y) {
                    point.y += stateGeometry.targetPoint.y;
                }
                geometry.targetPoint = Object.assign(new mx.mxPoint(), point);
            }

            // update the points of a line that define the lines curvature
            if (stateGeometry.points && !updatedPoints) {
                updatedPoints = true;
                if (!geometry.points) {
                    geometry.points = stateGeometry.points.map((point: mxPoint) => Object.assign(new mx.mxPoint(), point));
                } else {
                    geometry.points = stateGeometry.points.map((point: mxPoint, index: number) => {
                        if (index > geometry.points.length - 1) {
                            return Object.assign(new mx.mxPoint(), point)
                        }
                        const newPoint = { ...point }
                        if (geometry.points[index].x)
                            newPoint.x += geometry.points[index].x;
                        newPoint.y += geometry.points[index].y;
                        return Object.assign(new mx.mxPoint(), newPoint);
                    })
                }
            }

            // only apply geometry changes if a previous more important change hasn't already altered them
            if (initialGeometry.x === geometry.x) {
                geometry.x += stateGeometry.x || 0;
            }
            if (initialGeometry.y === geometry.y) {
                geometry.y += stateGeometry.y || 0;
            }
            if (initialGeometry.width === geometry.width) {
                geometry.width += stateGeometry.width || 0;
            }
            if (initialGeometry.height === geometry.height) {
                geometry.height += stateGeometry.height || 0;
            }
        }

        return Object.assign(cell.getGeometry(), geometry);
    }

    //mutates the base geometry
    public static addGeometries(base: mxGeometry, add: mxGeometry) {
        base.x += add.x || 0;
        base.y += add.y || 0;
        base.width += add.width || 0;
        base.height += add.height || 0;
        return base
    }

    public static getCellLabelForGlyphStates(cell: mxCell, baseState: GlyphState, states: GlyphState[]) {
        const mergedStates = [baseState, ...states]
        if (mergedStates.length <= 0) return
        mergedStates.reverse()
        for (const state of mergedStates) {
            const label = state.changes.find(change => change.cellId === cell.id)?.value;
            if (label !== undefined) return label
        }
        return ""
    }

    public static setVariableCellLabel(cell: mxCell, label: string | undefined | any) {
        if (label === undefined) {
            return;
        }
        if (cell.getValue() && typeof cell.getValue() === 'object' && cell.getValue().getAttribute) { // if value is an XML-node, change the node value
            cell.getValue().setAttribute("label", label);
        } else {
            cell.setValue(label);
        }
    }

    public static setCellTerminals(cell: mxCell, baseState: GlyphState, states: GlyphState[], model: mxGraphModel) {
        const mergedStates = [baseState, ...states]
        if (mergedStates.length <= 0) return
        mergedStates.reverse();
        let sourceFound = false;
        let targetFound = false;
        for (const state of mergedStates) {
            const source = state.changes.find(change => change.cellId === cell.id)?.source;
            const target = state.changes.find(change => change.cellId === cell.id)?.target;
            if (source && !sourceFound) {
                const sourceCell = model.getCell(source);
                sourceCell && cell.setTerminal(sourceCell, true);
                sourceFound = true;
            }
            if (target && !targetFound) {
                const targetCell = model.getCell(target);
                targetCell && cell.setTerminal(targetCell, false);
                targetFound = true;
            }
            if (targetFound && sourceFound) {
                return
            }
        }
    }

    //Returns a style object that only contains the key values that differ between one and another style string from mxGraph
    public static getStyleDifference(styleOld: string, styleNew: string) {
        const s1 = this.styleStringToObject(styleOld);
        const s2 = this.styleStringToObject(styleNew);
        for (const key of Object.keys(s1)) {
            if (s2[key] === s1[key]) {
                delete s2[key];
            } else if (s1[key] && !s2[key]) {
                s2[key] = "removed"
            }
        }
        return s2
    }

    //Returns an object that contains the difference between x,y,width,height of one and another mxGeometry.
    public static getGeometryDifference(defaultGeometry: any, newGeometry: any) {
        newGeometry = { ...newGeometry };
        if (!defaultGeometry) {
            return newGeometry;
        }
        for (const key of Object.keys(defaultGeometry)) {
            if (newGeometry[key] === defaultGeometry[key]) {
                delete newGeometry[key];
            }
        }
        if (newGeometry.x) {
            newGeometry.x -= defaultGeometry.x;
        } else {
            //newGeometry.x = 0;
        }
        if (newGeometry.y) {
            newGeometry.y -= defaultGeometry.y;
        } else {
            //newGeometry.y = 0;
        }
        if (newGeometry.width) {
            newGeometry.width -= defaultGeometry.width;
        } else {
            //newGeometry.width = 0;
        }
        if (newGeometry.height) {
            newGeometry.height -= defaultGeometry.height;
        } else {
            //newGeometry.height = 0;
        }

        if (defaultGeometry.sourcePoint && newGeometry.sourcePoint) {
            newGeometry.sourcePoint = this.getPointDifference(defaultGeometry.sourcePoint, newGeometry.sourcePoint);
        }
        if (defaultGeometry.targetPoint && newGeometry.targetPoint) {
            newGeometry.targetPoint = this.getPointDifference(defaultGeometry.targetPoint, newGeometry.targetPoint);
        }

        if (newGeometry.points) {
            newGeometry.points = this.getPointsDifference(defaultGeometry.points, newGeometry.points)
        }

        return newGeometry
    }

    public static getPointsDifference(defaultPoints: mxPoint[], newPoints: mxPoint[]) {
        if (!defaultPoints) {
            return newPoints
        }
        return newPoints.map((newPoint, index) => {
            if (index > defaultPoints.length - 1) {
                return newPoint
            }
            return this.getPointDifference(defaultPoints[index], newPoint)
        })
    }

    public static getPointDifference(defaultPoint: mxPoint, newPoint: mxPoint) {
        const difference = { ...newPoint };
        difference.x -= defaultPoint.x;
        difference.y -= defaultPoint.y;
        return difference;
    }

    public static cellIsVisibleInNoState(cellId: string, states: GlyphState[]) {
        for (const state of states) {
            const change = state.changes.find(c => c.cellId === cellId)?.style
            if (!change || (!change.hide || change.hide === "0")) {
                return false;
            }
        }
        return true;
    }

    public static getChildOfCellById(cell: mxCell, childId: string): any {
        if (cell.id === childId) return cell;
        if (!cell.children) return undefined;
        for (const child of cell.children) {
            const found = this.getChildOfCellById(child, childId);
            if (found) {
                return found;
            }
        }
        return undefined
    }

    public static getAllChildrenOfCell(cell: mxCell): mxCell[] {
        if (!cell) {
            return [];
        }
        let cells: mxCell[] = [cell];
        if (cell.children && cell.children.length > 0) {
            for (const c of cell.children) {
                cells = [...cells, ...this.getAllChildrenOfCell(c)];
            }
        }
        return cells;
    }

    public static unlinkCellFromIdeaboxEntry(cell: mxCell, entry: IdeaBoxEntry) {
        this.linkCellToIdeaBoxEntry(cell, entry, true);
    }

    public static linkCellToIdeaBoxEntry(cell: mxCell, entry: IdeaBoxEntry, remove?: boolean) {
        if (!entry) {
            return;
        }

        // Update cell entries
        if (!remove) {
            !entry.linkedCells.find((id: string) => cell.id === id) && entry.linkedCells.push(cell.id);
        } else {
            entry.linkedCells = entry.linkedCells.filter((id: string) => id !== cell.id);
        }
    }

    public static exportGraph(graph: mxGraph) {
        //var pageCount = mx.mxUtils.prompt('Enter page count', '1');
        let pageCount = 1;
        if (pageCount != null) {
            var scale = mx.mxUtils.getScaleForPageCount(pageCount, graph);
            var preview = new mx.mxPrintPreview(graph, scale);
            preview.title = "HMI Preview";
            preview.open();
            domtoimage.toBlob(preview.getWindow().document.getElementById("mxPage-1"))
                .then(function (blob) {
                    window.saveAs(blob, 'hmi.png');
                });
        }
    }

    public static async exportAllGlobalStatesAsZip(
        graph: any,
        project: any,
        renderGlobalState: (state: any, localState?: any) => void,
        currentGlobalState: any,
        onProgress?: (message: string) => void
    ) {
        const globalStates = project.globalStates;
        const glyphs = project.glyphs || [];

        if (!globalStates || globalStates.length === 0) {
            console.warn('No global states to export');
            return;
        }

        console.log(`Starting export of all states...`);

        const zip = new JSZip();
        const failedExports: string[] = [];

        // Check if graph container exists
        if (!graph.container) {
            console.error('Graph container not found');
            return;
        }

        let exportedCount = 0;
        let totalStates = 0;

        // Calculate total states to export
        for (const globalState of globalStates) {
            const isDefaultGlobalState = globalState.id === "default" || globalState.name === "default";
            if (isDefaultGlobalState) {
                // For default global state, count all glyph states
                glyphs.forEach((glyph: any) => {
                    if (glyph.states) {
                        totalStates += glyph.states.length;
                    }
                });
            } else {
                // For non-default global states, just one export per state
                totalStates += 1;
            }
        }

        console.log(`Total states to export: ${totalStates}`);
        onProgress?.(`Exporting ${totalStates} states...`);

        // Export each global state
        for (const globalState of globalStates) {
            const isDefaultGlobalState = globalState.id === "default" || globalState.name === "default";

            if (isDefaultGlobalState) {
                // For default global state: export all glyph states
                console.log(`Exporting default global state - iterating over all glyph states...`);

                for (let g = 0; g < glyphs.length; g++) {
                    const glyph = glyphs[g];
                    if (!glyph.states || glyph.states.length === 0) continue;

                    for (let i = 0; i < glyph.states.length; i++) {
                        const localState = glyph.states[i];
                        exportedCount++;
                        const progressMsg = `Exporting ${exportedCount}/${totalStates}: ${glyph.name} - ${localState.name || localState.id}`;
                        console.log(progressMsg);
                        onProgress?.(progressMsg);

                        try {
                            // Small delay after progress update to let React update UI
                            await new Promise(resolve => setTimeout(resolve, 50));

                            // Render this local state
                            // For default state, don't pass it as additional state - just render the global state
                            if (localState.id === "default") {
                                renderGlobalState(globalState);
                            } else {
                                renderGlobalState(globalState, localState);
                            }

                            // Force graph refresh
                            graph.refresh();
                            graph.view.validate();

                            // Wait for render to complete (DOM update)
                            await new Promise(resolve => setTimeout(resolve, 400));

                            // Generate filename
                            const glyphName = glyph.name || `glyph-${g + 1}`;
                            const stateName = localState.name || `state-${i + 1}`;
                            const sanitizedGlyphName = glyphName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
                            const sanitizedStateName = stateName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
                            const filename = `hmi-${sanitizedGlyphName}-${sanitizedStateName}.png`;

                            // Convert graph container to blob (no pop-up needed!)
                            const blob = await domtoimage.toBlob(graph.container);

                            // Add to ZIP
                            zip.file(filename, blob);
                            console.log(`Successfully exported: ${filename}`);

                            // Small delay before next export
                            await new Promise(resolve => setTimeout(resolve, 100));

                        } catch (error) {
                            console.error(`Failed to export ${glyph.name} - ${localState.name || localState.id}:`, error);
                            failedExports.push(`${glyph.name}-${localState.name || localState.id}`);
                        }
                    }
                }
            } else {
                // For non-default global states: just export the fixed selection
                exportedCount++;
                const progressMsg = `Exporting ${exportedCount}/${totalStates}: global state ${globalState.name || globalState.id}`;
                console.log(progressMsg);
                onProgress?.(progressMsg);

                try {
                    // Small delay after progress update to let React update UI
                    await new Promise(resolve => setTimeout(resolve, 50));

                    // Render this global state
                    renderGlobalState(globalState);

                    // Force graph refresh
                    graph.refresh();
                    graph.view.validate();

                    // Wait for render to complete (DOM update)
                    await new Promise(resolve => setTimeout(resolve, 400));

                    // Generate filename
                    const stateName = globalState.name || globalState.id;
                    const sanitizedName = stateName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
                    const filename = `hmi-global-${sanitizedName}.png`;

                    // Convert graph container to blob (no pop-up needed!)
                    const blob = await domtoimage.toBlob(graph.container);

                    // Add to ZIP
                    zip.file(filename, blob);
                    console.log(`Successfully exported: ${filename}`);

                    // Small delay before next export
                    await new Promise(resolve => setTimeout(resolve, 100));

                } catch (error) {
                    console.error(`Failed to export global state ${globalState.name || globalState.id}:`, error);
                    failedExports.push(`global-${globalState.name || globalState.id}`);
                }
            }
        }

        // Restore original state
        console.log('Restoring original state...');
        onProgress?.('Restoring original state...');
        renderGlobalState(currentGlobalState);
        graph.refresh();
        graph.view.validate();

        // Generate and download ZIP file
        try {
            console.log('Generating ZIP file...');
            onProgress?.('Generating ZIP file...');
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            window.saveAs(zipBlob, 'hmi-all-states.zip');

            if (failedExports.length === 0) {
                console.log(`Successfully exported ${totalStates} states as ZIP`);
            } else {
                console.warn(`Exported ${totalStates - failedExports.length}/${totalStates} states. Failed: ${failedExports.join(', ')}`);
            }
        } catch (error) {
            console.error('Failed to generate ZIP file:', error);
        }
    }

    public static isCellInvisible(cell: mxCell) {
        return !cell.visible
    }

    public static enableCell(cell: any) {
        this.disableCell(cell, true);
    }

    public static disableCell(cell: any, enable?: boolean) {
        if (!cell) return;
        cell.setConnectable(enable);
        let style = cell.getStyle() ? cell.getStyle() : "";
        if (enable) {
            style = style.replaceAll("locked=1;", "");
        } else {
            style += "locked=1;"
        }
        cell.setStyle(style);
        /** also locks children if needed
        if (!cell.children) return;
        for (const child of cell.children) {
            disableCell(child, enable);
        }
        */
    };

    // This function enables using labels with a graph
    public static addIdeaBoxEntryLabelSupport(graph: any, iframe: any, ideabox: IdeaBox) {
        graph.setExtendParents(true);
        graph.setExtendParentsOnAdd(true);
        graph.setExtendParentsOnMove(true);
        graph.setAllowNegativeCoordinates(true);

        //////////////// Show labels upon hovering

        var selection: mxCell[] = [];

        function getCellsOfIdeaBoxEntry(entry: IdeaBoxEntry) {
            if (!entry.linkedCells || entry.linkedCells.length <= 0) return []
            const model = graph.getModel();
            const cells = [];
            for (const id of entry.linkedCells) {
                const cell = model.getCell(id);
                cell && cells.push(cell);
            }
            return cells;
        }


        function drawLabels(labels: mxCell[]) {
            for (const cell of labels) {
                drawLabel(cell);
            }
            removeInactiveLabels(labels);
        };

        function getIdeaBoxEntriesOfCell(cell: mxCell) {
            const entries = []
            for (const entry of ideabox.entries) {
                if (entry.linkedCells.find(cellId => cellId === cell.id)) {
                    entries.push(entry);
                }
            }
            return entries;
        }

        graph.getSelectionModel().addListener(mx.mxEvent.CHANGE, (g: any) => {
            selection = [...g.cells];
            drawLabels(selection);
        });


        graph.addListener(mx.mxEvent.CELLS_MOVED, (sender: any, evt: any) => {
            setTimeout(() => { drawLabels(evt.properties.cells); }, 1)
        });


        graph.addListener(mx.mxEvent.CELLS_RESIZED, (sender: any, evt: any) => {
            setTimeout(() => { drawLabels(evt.properties.cells); }, 1)
        });

        graph.highlightCellsWithEntry = function (entry: IdeaBoxEntry, highlight: boolean) {
            const cells = getCellsOfIdeaBoxEntry(entry);
            if (cells.length <= 0) return;
            if (highlight) {
                drawLabels(cells);
            } else {
                drawLabels(selection);
            }
        }

        //Remove display of tooltips
        graph.getTooltipForCell = () => { };

        // Changes label visibility on hover
        let mouseDown = false;
        graph.addMouseListener({
            currentState: null,
            previousStyle: null,
            mouseDown: function (sender: any, me: any) { },
            mouseUp: function (sender: any, me: any) { mouseDown = false },
            dragEnter: function (evt: any, state: any) { },
            dragLeave: function (evt: any, state: any) { },
            mouseMove: function (sender: any, me: any) {
                if (mouseDown || (this.currentState != null && me.getState() === this.currentState)) {
                    return;
                }
                var tmp = graph.view.getState(me.getCell());

                if (graph.isMouseDown) {
                    mouseDown = true;
                    graph.removeAllLabels();
                    tmp = null;
                    return;
                }

                if (tmp !== this.currentState) {
                    this.currentState = tmp;

                    if (this.currentState != null) {
                        drawLabels([...selection, this.currentState.cell]);
                        GlyphEventService.dispatch(GlyphEvent.HIGHLIGHT_ENTRIES, getIdeaBoxEntriesOfCell(this.currentState.cell));
                    } else {
                        drawLabels(selection)
                        GlyphEventService.dispatch(GlyphEvent.HIGHLIGHT_ENTRIES, []);
                    }
                }
            }
        });

        // INIT SECOND LABEL

        // Hook for returning the label of the cell
        graph.getLabelForIdeaBoxEntries = function (entries: IdeaBoxEntry[]) {
            let cellLabels = '';
            for (const entry of entries) {
                const color = entry.type.includes(TaskType.INFORMATION) ? "#ffc107" : "#7edeea";
                const interfaceName = entry?.interfaceElement.split(".").reverse()[0];
                cellLabels += `<div id="glyph${nanoid()}" class="konect-label" style="background-color: ${color};">` +
                    `<span>${entry.task.name}</span>` +
                    `</br>` +
                    `<span style="font-weight: bold;">${interfaceName}</span>` +
                    `<div><span style="border-bottom-style: solid;border-width: 1px;border-color: #b8b8b8;display: block;">${entry.insight.name}</span>` +
                    `<span style="display: block;">${entry.selectedAttribute.name}</span>` +
                    `<span class="delete-button"><span class="geSprite geSprite-delete"/></span>` +
                    `</div>` +
                    `</div>`
            }

            let labels = `<div class="konect-label-container">
            <div style="display: inline-flex; pointer-events: all">
                ${cellLabels}
            </div>
            </div>`
            return labels;
        };

        var relativeChildVerticesVisible = true;

        // Overrides method to hide relative child vertices
        graph.isCellVisible = function (cell: mxCell) {
            return (!this.model.isVertex(cell) || cell.geometry === null ||
                !cell.geometry.relative ||
                cell.geometry.relative === relativeChildVerticesVisible) && cell.visible;
        };


        /** DEPRECATED use this if a second cell label should be reintroduced for whatever reason
        graph.cellRenderer.getShapesForState = function (state: any) {
            return [state.shape, state.text, state.secondLabel, state.control];
        };
         */

        graph.setCellOpacity = (cell: any, value: number) => {
            if (!cell) return;
            const cellState = graph.view.getState(cell);
            if (cellState && cellState.shape && cellState.shape.node) {
                const node = cellState.shape.node;
                node.setAttribute("opacity", value);
            }
            if (!cell.children) return;
            for (const child of cell.children) {
                graph.setCellOpacity(child, value);
            }
        }

        function removeInactiveLabels(currentSelection: mxCell[]) {
            const svg = graph.view.getDrawPane().ownerSVGElement;
            for (const foreign of [...Array.from(svg.children).filter((child: any) => child.tagName === "foreignObject")]) {
                //@ts-ignore
                if (currentSelection.find(cell => "label" + cell.id === foreign.id)) {
                    continue
                }
                //@ts-ignore
                foreign.remove();
            }
        }

        graph.removeAllLabels = () => {
            const svg = graph.view.getDrawPane().ownerSVGElement;
            for (const foreign of [...Array.from(svg.children).filter((child: any) => child.tagName === "foreignObject")]) {
                //@ts-ignore
                foreign.remove();
            }
        }

        function drawLabel(cell: mxCell) {
            if (!cell) return;
            const entries = getIdeaBoxEntriesOfCell(cell);
            if (entries.length <= 0) return;

            const svg = graph.view.getDrawPane().ownerSVGElement;
            let foreign: any = svg.getElementById("label" + cell.id);

            // if label is already drawn skip drawing it
            if (foreign) return;

            foreign = document.createElementNS('http://www.w3.org/2000/svg', "foreignObject");
            foreign.style.pointerEvents = "none";
            //@ts-ignore
            foreign.classname = "label-container-node";
            foreign.setAttribute('width', "10000px");
            foreign.setAttribute('height', "10000px");
            foreign.id = "label" + cell.id;
            svg.appendChild(foreign);

            foreign.innerHTML = graph.getLabelForIdeaBoxEntries(entries);

            //Add onclick to label to remove it.
            const buttons = foreign.getElementsByClassName('delete-button');
            entries.forEach((entry: IdeaBoxEntry, index: number) => {
                buttons[index].onclick = (e: any) => {
                    drawLabels([]);
                    graph.onRemoveLinkFromCell(cell, entry);
                }
            })

            const state = graph.view.getState(cell);
            if (!state) return;
            foreign.style.display = "block";
            foreign.setAttribute('x', (state.x + state.width / 2 - 5000));
            foreign.setAttribute('y', (state.y + state.height + 10 - 5000));

            // add drag and drop functionality to labels
            const subLabels = foreign.getElementsByClassName('konect-label');
            let index = 0;
            for (const l of subLabels) {
                const entry = entries[index];
                const handleMove = (event: any) => {
                    const rect = foreign.getBoundingClientRect();
                    l.style.position = "absolute";
                    l.style.left = "0";
                    l.style.top = "0";
                    l.style.opacity = "0.5 !important";
                    l.style.transform = "translate(" + (event.clientX - rect.x + 10) + "px," + (event.clientY - rect.y + 10 - 5000) + "px)";
                    l.style.pointerEvents = "none";
                    foreign.style.pointerEvents = "all";
                    foreign.style.cursor = "crosshair"
                    foreign.style.opacity = "0.5";
                }

                l.onmousedown = (event: any) => {
                    if (event.target.className.includes("delete")) return;
                    iframe.addEventListener("mousemove", handleMove);
                    handleMove(event)

                    const handleMouseUp = (event: any) => {
                        l.style.position = "relative";
                        l.style.transform = "translate(0px, 0px)";
                        l.style.pointerEvents = "all";
                        iframe.removeEventListener("mousemove", handleMove);
                        iframe.removeEventListener("mouseup", handleMouseUp);
                        foreign.style.pointerEvents = "none";
                        foreign.style.opacity = "1";
                        event.entry = entry;
                        graph.handleLabelDrop && graph.handleLabelDrop(event, cell);
                    }
                    iframe.addEventListener("mouseup", handleMouseUp);
                }
                index++;
            }
        }
    }
}

